import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionPlan, Subscription } from '../../types/subscription';
import PricingCard from './PricingCard';

interface PricingPlansProps {
  plans: SubscriptionPlan[];
  currentSubscription?: Subscription;
  onSubscriptionChange: () => void;
}

export const PricingPlans: React.FC<PricingPlansProps> = ({
  plans,
  currentSubscription,
  onSubscriptionChange,
}) => {
  void onSubscriptionChange;
  const navigate = useNavigate();

  const planOrder = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'];
  const sortedPlans = [...plans].sort((a, b) => {
    const sortDelta = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
    if (sortDelta !== 0) return sortDelta;
    return planOrder.indexOf(a.planType) - planOrder.indexOf(b.planType);
  });

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (currentSubscription?.planId === plan.id) return;
    navigate('/subscription/payment', {
      state: {
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        currency: plan.currency || 'INR',
        billingCycle: plan.billingCycle || 'monthly',
        mode: currentSubscription ? 'upgrade' : 'new',
      },
    });
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Plan comparison</p>
            <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">Choose a plan with clearer tradeoffs</h3>
            <p className="mt-2 text-sm text-slate-500">
              The pricing grid now emphasizes the real differences between tiers: attempts, reporting depth, certification access, and support level.
            </p>
          </div>
          <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            {sortedPlans.length} plans available
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-4 md:grid-cols-2">
        {sortedPlans.map((plan) => (
          <PricingCard
            key={plan.id}
            plan={plan}
            isCurrentPlan={currentSubscription?.planId === plan.id}
            onSelect={() => handleSelectPlan(plan)}
            loading={false}
            billingCycle={plan.billingCycle || 'monthly'}
          />
        ))}
      </div>
    </div>
  );
};

export default PricingPlans;
